import type { NativeHomeConfig } from "./homeConfig";

export type HomeUserView = { Id?: string; Name?: string; CollectionType?: string };

export function buildHomeUserViewsSourceKey(
  serverUrl: string,
  userId: string,
  views: HomeUserView[],
  defaultDisabledIds: string[] = [],
) {
  const inventory = views.flatMap((view) => {
    const id = view.Id?.trim();
    const name = view.Name?.trim();
    if (!id || !name) return [];
    return [[id, name, view.CollectionType?.toLocaleLowerCase() ?? ""]];
  });
  return JSON.stringify([
    serverUrl.replace(/\/$/, ""),
    userId,
    inventory,
    [...new Set(defaultDisabledIds)].sort(),
  ]);
}

export function orderHomeUserViews(views: HomeUserView[], orderedIds: string[] = []) {
  if (!orderedIds.length) return views;
  const order = new Map(orderedIds.map((id, index) => [id, index]));
  return views
    .map((view, sourceIndex) => ({ view, sourceIndex }))
    .sort((left, right) => {
      const leftOrder = left.view.Id ? order.get(left.view.Id) : undefined;
      const rightOrder = right.view.Id ? order.get(right.view.Id) : undefined;
      if (leftOrder === undefined && rightOrder === undefined) return left.sourceIndex - right.sourceIndex;
      if (leftOrder === undefined) return 1;
      if (rightOrder === undefined) return -1;
      return leftOrder - rightOrder;
    })
    .map(({ view }) => view);
}

export function mergeHomeConfigUserViews(
  config: NativeHomeConfig,
  views: HomeUserView[],
  sourceKey: string,
  defaultDisabledIds: string[] = [],
) {
  if (!sourceKey || config.userViewsSourceKey === sourceKey) return config;
  const disabled = new Set(defaultDisabledIds);
  const libraries = views.flatMap((view) => {
    const libraryId = view.Id?.trim();
    const libraryName = view.Name?.trim();
    if (!libraryId || !libraryName) return [];
    const exactPrior = config.libraryToggles.find((entry) => entry.libraryId === libraryId);
    const migratedPrior = config.userViewsSourceKey
      ? undefined
      : config.libraryToggles.find((entry) => entry.libraryName.toLocaleLowerCase() === libraryName.toLocaleLowerCase());
    const prior = exactPrior ?? migratedPrior;
    return [{
      libraryId,
      libraryName,
      collectionType: view.CollectionType?.toLocaleLowerCase(),
      enabled: prior ? prior.enabled !== false : !disabled.has(libraryId),
    }];
  });
  return {
    ...config,
    version: 4,
    userViewsSourceKey: sourceKey,
    libraryToggles: libraries.length ? libraries : config.libraryToggles,
  };
}
